import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Clock, Thermometer, CircleGauge, Waves, Droplet, Flame,
    AlertTriangle, LineChart, TrendingUp, Inbox,
    ChevronDown, Loader2, CheckCircle2
} from 'lucide-react';

// ============================================
// ✅ Карта иконок для групп показаний
// ============================================
const GROUP_ICONS = {
    'Время и дата': Clock,
    'Температура': Thermometer,
    'Давление': CircleGauge,
    'Объемный расход': Droplet,
    'Массовый расход': Waves,
    'Тепловая энергия': Flame,
    'Нештатные ситуации': AlertTriangle,
    'Объем': Droplet,
    'Масса': Waves,
    'Энергия': Flame,
    'Время': Clock,
};

const GROUP_COLORS = {
    'Время и дата': 'border-blue-500/30 text-blue-400',
    'Температура': 'border-red-500/30 text-red-400',
    'Давление': 'border-cyan-500/30 text-cyan-400',
    'Объемный расход': 'border-purple-500/30 text-purple-400',
    'Массовый расход': 'border-indigo-500/30 text-indigo-400',
    'Тепловая энергия': 'border-orange-500/30 text-orange-400',
    'Нештатные ситуации': 'border-yellow-500/30 text-yellow-400',
    'Объем': 'border-teal-500/30 text-teal-400',
    'Масса': 'border-indigo-500/30 text-indigo-400',
    'Энергия': 'border-orange-500/30 text-orange-400',
    'Время': 'border-blue-500/30 text-blue-400',
};

// ============================================
// ✅ Форматирование числовых значений с запятой
// ============================================
const formatValue = (val, decimals = 3) => {
    if (val === null || val === undefined) return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === -1) return 'НС';
    return num.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

// ============================================
// ✅ Компонент группы показаний (как в Аксиома-01)
// ============================================
const ReadingsGroup = ({ group }) => {
    const [isOpen, setIsOpen] = useState(true);
    const isNSGroup = group.name === 'Нештатные ситуации';

    const IconComponent = GROUP_ICONS[group.name] || Clock;
    const colorClass = GROUP_COLORS[group.name] || 'border-slate-700 text-slate-400';
    const [borderColor, iconColor] = colorClass.split(' ');

    return (
        <div className={`bg-slate-900 rounded-lg border ${borderColor} overflow-hidden`}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/50 transition"
            >
                <div className="flex items-center gap-2">
                    <IconComponent className={`w-4 h-4 ${iconColor}`} />
                    <h3 className="text-white font-semibold text-sm">{group.name}</h3>
                    <span className="text-slate-500 text-xs">({group.items.length})</span>
                </div>
                <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>
            {isOpen && (
                <div className="border-t border-slate-700">
                    {group.items.length === 0 ? (
                        <div className="px-4 py-6 text-center text-slate-500 text-sm">
                            {isNSGroup ? (
                                <div className="flex flex-col items-center gap-2">
                                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                                    <span>Нештатных ситуаций нет</span>
                                </div>
                            ) : (
                                'Нет данных'
                            )}
                        </div>
                    ) : isNSGroup ? (
                        <div className="divide-y divide-slate-700/50">
                            {group.items.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="px-4 py-3 text-sm bg-red-500/10 hover:bg-red-500/20 transition flex items-start gap-3"
                                >
                                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-red-200 font-medium flex-1">
                                        {item.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-800/50 text-xs text-slate-400 font-semibold">
                                <div className="col-span-5 sm:col-span-6">Параметр</div>
                                <div className="col-span-4 sm:col-span-4 text-center">Значение</div>
                                <div className="col-span-3 sm:col-span-2 text-right">Ед. изм.</div>
                            </div>
                            <div className="divide-y divide-slate-700/50">
                                {group.items.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className={`grid grid-cols-12 gap-2 px-4 py-2 text-sm hover:bg-slate-800/30 transition ${item.isNS ? 'bg-red-500/10' : ''}`}
                                    >
                                        <div className="col-span-5 sm:col-span-6 text-slate-300 truncate" title={item.name}>
                                            {item.name}
                                        </div>
                                        <div className={`col-span-4 sm:col-span-4 text-center font-mono ${item.isNS ? 'text-red-400 font-bold' : 'text-white'}`}>
                                            {item.value}
                                        </div>
                                        <div className="col-span-3 sm:col-span-2 text-right text-slate-400 text-xs">
                                            {item.unit}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
// ============================================
// ✅ Построение групп для ТЕКУЩИХ показаний
// ============================================
const buildCurrentGroups = (readings) => {
    if (!readings) return [];

    const groups = {};
    const values = readings.parameters?.values || {};
    const errors = readings.parameters?.errors || [];

    const addToGroup = (groupName, name, value, unit) => {
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push({ name, value, unit, isNS: value === 'НС' });
    };

    // ✅ Группа "Время" — первой строкой "Показания на"
    addToGroup('Время', 'Показания на', readings.datetime || '—', '');

    // Время (в часах, уже из БД)
    if (values['Narabotka'] !== undefined) addToGroup('Время', 'Наработка', formatValue(values['Narabotka']), 'час');
    if (values['Tmin'] !== undefined) addToGroup('Время', 'Tmin', formatValue(values['Tmin']), 'час');
    if (values['Tmax'] !== undefined) addToGroup('Время', 'Tmax', formatValue(values['Tmax']), 'час');
    if (values['Tdt'] !== undefined) addToGroup('Время', 'Tdt', formatValue(values['Tdt']), 'час');
    if (values['Tf'] !== undefined) addToGroup('Время', 'Tf', formatValue(values['Tf']), 'час');
    if (values['Tep'] !== undefined) addToGroup('Время', 'Tep', formatValue(values['Tep']), 'час');
    

    // Температура
    if (values['T1'] !== undefined) addToGroup('Температура', 'T1', formatValue(values['T1']), '°C');
    if (values['T2'] !== undefined) addToGroup('Температура', 'T2', formatValue(values['T2']), '°C');
    if (values['Td1'] !== undefined) addToGroup('Температура', 'Td1', formatValue(values['Td1']), '°C');

    // Давление
    if (values['P1'] !== undefined) addToGroup('Давление', 'P1', formatValue(values['P1']), 'МПа');
    if (values['P2'] !== undefined) addToGroup('Давление', 'P2', formatValue(values['P2']), 'МПа');
    if (values['P3'] !== undefined) addToGroup('Давление', 'P3', formatValue(values['P3']), 'МПа');
    if (values['P4'] !== undefined) addToGroup('Давление', 'P4', formatValue(values['P4']), 'МПа');

    // Объёмный расход
    if (values['V1'] !== undefined) addToGroup('Объёмный расход', 'V1', formatValue(values['V1']), 'м³/ч');
    if (values['V2'] !== undefined) addToGroup('Объёмный расход', 'V2', formatValue(values['V2']), 'м³/ч');
    if (values['V3'] !== undefined) addToGroup('Объёмный расход', 'V3', formatValue(values['V3']), 'м³/ч');
    if (values['V4'] !== undefined) addToGroup('Объёмный расход', 'V4', formatValue(values['V4']), 'м³/ч');

    // Массовый расход
    if (values['G1'] !== undefined) addToGroup('Массовый расход', 'G1', formatValue(values['G1']), 'т/ч');
    if (values['G2'] !== undefined) addToGroup('Массовый расход', 'G2', formatValue(values['G2']), 'т/ч');
    if (values['Gd1'] !== undefined) addToGroup('Массовый расход', 'Gd1', formatValue(values['Gd1']), 'т/ч');

    // Тепловая энергия
    if (values['Q1'] !== undefined) addToGroup('Тепловая энергия', 'Q1', formatValue(values['Q1']), 'Гкал/ч');
    if (values['Q1min'] !== undefined) addToGroup('Тепловая энергия', 'Q1min', formatValue(values['Q1min']), 'Гкал/ч');
    if (values['Q1max'] !== undefined) addToGroup('Тепловая энергия', 'Q1max', formatValue(values['Q1max']), 'Гкал/ч');
    if (values['Q2'] !== undefined) addToGroup('Тепловая энергия', 'Q2', formatValue(values['Q2']), 'Гкал/ч');
    if (values['Q2min'] !== undefined) addToGroup('Тепловая энергия', 'Q2min', formatValue(values['Q2min']), 'Гкал/ч');
    if (values['Q2max'] !== undefined) addToGroup('Тепловая энергия', 'Q2max', formatValue(values['Q2max']), 'Гкал/ч');
    if (values['Q3'] !== undefined) addToGroup('Тепловая энергия', 'Q3', formatValue(values['Q3']), 'Гкал/ч');

    // Нештатные ситуации
    if (errors && errors.length > 0) {
        groups['Нештатные ситуации'] = errors.map(err => ({ name: err, value: '', unit: '', isNS: true }));
    }

    return Object.entries(groups).map(([name, items]) => ({ name, items }));
};

// ============================================
// ✅ Построение групп для НАКОПЛЕННЫХ показаний
// ============================================
const buildAccumulatedGroups = (readings) => {
    if (!readings) return [];

    const groups = {};
    const values = readings.parameters?.values || {};
    const errors = readings.parameters?.errors || [];

    const addToGroup = (groupName, name, value, unit) => {
        if (!groups[groupName]) groups[groupName] = [];
        groups[groupName].push({ name, value, unit });
    };

    // ✅ Группа "Время" — первой строкой "Показания на"
    addToGroup('Время', 'Показания на', readings.datetime || '—', '');

    // Время (в часах, уже из БД)
    if (values['Narabotka'] !== undefined) addToGroup('Время', 'Наработка', formatValue(values['Narabotka']), 'час');
    if (values['Tmin'] !== undefined) addToGroup('Время', 'Tmin', formatValue(values['Tmin']), 'час');
    if (values['Tmax'] !== undefined) addToGroup('Время', 'Tmax', formatValue(values['Tmax']), 'час');
    if (values['Tdt'] !== undefined) addToGroup('Время', 'Tdt', formatValue(values['Tdt']), 'час');
    if (values['Tf'] !== undefined) addToGroup('Время', 'Tf', formatValue(values['Tf']), 'час');
    if (values['Tep'] !== undefined) addToGroup('Время', 'Tep', formatValue(values['Tep']), 'час');
    

    // Объём
    if (values['V1'] !== undefined) addToGroup('Объём', 'V1', formatValue(values['V1']), 'м³');
    if (values['V2'] !== undefined) addToGroup('Объём', 'V2', formatValue(values['V2']), 'м³');
    if (values['V3'] !== undefined) addToGroup('Объём', 'V3', formatValue(values['V3']), 'м³');
    if (values['V4'] !== undefined) addToGroup('Объём', 'V4', formatValue(values['V4']), 'м³');

    // Масса
    if (values['G1'] !== undefined) addToGroup('Масса', 'G1', formatValue(values['G1']), 'т');
    if (values['G2'] !== undefined) addToGroup('Масса', 'G2', formatValue(values['G2']), 'т');
    if (values['Gd1'] !== undefined) addToGroup('Масса', 'Gd1', formatValue(values['Gd1']), 'т');

    // Энергия
    if (values['Q1'] !== undefined) addToGroup('Энергия', 'Q1', formatValue(values['Q1']), 'Гкал');
    if (values['Q1min'] !== undefined) addToGroup('Энергия', 'Q1min', formatValue(values['Q1min']), 'Гкал');
    if (values['Q1max'] !== undefined) addToGroup('Энергия', 'Q1max', formatValue(values['Q1max']), 'Гкал');
    if (values['Q2'] !== undefined) addToGroup('Энергия', 'Q2', formatValue(values['Q2']), 'Гкал');
    if (values['Q2min'] !== undefined) addToGroup('Энергия', 'Q2min', formatValue(values['Q2min']), 'Гкал');
    if (values['Q2max'] !== undefined) addToGroup('Энергия', 'Q2max', formatValue(values['Q2max']), 'Гкал');
    if (values['Q3'] !== undefined) addToGroup('Энергия', 'Q3', formatValue(values['Q3']), 'Гкал');


    return Object.entries(groups).map(([name, items]) => ({ name, items }));
};

// ============================================
// ✅ Компонент текущих показаний
// ============================================
const CurrentView = ({ readings }) => {
    if (!readings) {
        return (
            <div className="text-center py-12 text-slate-400">
                <Inbox className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p>Нет данных</p>
            </div>
        );
    }

    const groups = buildCurrentGroups(readings);

    return (
        <div className="space-y-4">
            {groups.map((group, idx) => (
                <ReadingsGroup key={idx} group={group} />
            ))}
        </div>
    );
};

// ============================================
// ✅ Компонент накопленных показаний
// ============================================
const AccumulatedView = ({ readings }) => {
    if (!readings) {
        return (
            <div className="text-center py-12 text-slate-400">
                <Inbox className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p>Нет данных</p>
            </div>
        );
    }

    const groups = buildAccumulatedGroups(readings);

    return (
        <div className="space-y-4">
            {groups.map((group, idx) => (
                <ReadingsGroup key={idx} group={group} />
            ))}
        </div>
    );
};

// ============================================
// ✅ Главный компонент вкладки "Показания"
// ============================================
const ReadingsTab = ({ deviceName }) => {
    const [activeSubTab, setActiveSubTab] = useState('current');
    const [currentReadings, setCurrentReadings] = useState(null);
    const [accumulatedReadings, setAccumulatedReadings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (deviceName) {
            loadReadings();
        }
    }, [deviceName]);

    const loadReadings = async () => {
        if (!deviceName) return;

        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';

            const [currentRes, accumulatedRes] = await Promise.all([
                axios.get(`${API_URL}/api/devices/karat307/${deviceName}/readings/current`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/api/devices/karat307/${deviceName}/readings/accumulated`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);
            console.log('📋 Карат-307 Показания текущие:', currentRes.data);
            setCurrentReadings(currentRes.data);
            console.log('📋 Карат-307 Показания накопленные:', accumulatedRes.data);
            setAccumulatedReadings(accumulatedRes.data);
        } catch (err) {
            console.error('Ошибка загрузки показаний:', err);
            setError('Не удалось загрузить показания');
        } finally {
            setIsLoading(false);
        }
    };

    if (!deviceName) {
        return (
            <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                <p className="text-slate-400">Устройство не выбрано</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <Loader2 className="animate-spin h-8 w-8 text-green-500 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Загрузка показаний...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Подвкладки */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                <div className="flex border-b border-slate-700">
                    <button
                        onClick={() => setActiveSubTab('current')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition flex items-center justify-center gap-2 ${activeSubTab === 'current'
                            ? 'bg-green-600/20 text-green-300 border-b-2 border-green-500'
                            : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                            }`}
                    >
                        <LineChart className="w-4 h-4" />
                        Текущие
                    </button>
                    <button
                        onClick={() => setActiveSubTab('accumulated')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition flex items-center justify-center gap-2 ${activeSubTab === 'accumulated'
                            ? 'bg-green-600/20 text-green-300 border-b-2 border-green-500'
                            : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                            }`}
                    >
                        <TrendingUp className="w-4 h-4" />
                        Накопленные
                    </button>
                </div>

                <div className="p-4">
                    {activeSubTab === 'current' ? (
                        <CurrentView readings={currentReadings} />
                    ) : (
                        <AccumulatedView readings={accumulatedReadings} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReadingsTab;